import {
  createForm,
  createVectorMapLayerGroup,
  FormElement,
  MAP_PROJECTION_3857,
  MAP_PROJECTION_4326,
  mergeExtents,
  transformExtent,
  useForm,
  useModals,
  useNotifications,
  type VectorMapLayerConfig
} from '@common';
import ParcelGroupModal from '../../parcel/components/parcel-group-modal/ParcelGroupModal.vue';
import type { ParcelGroupModalConfig } from '../../parcel/models/parcel-group-modal.model';
import { useHeightmapWizardMap } from './heightmap-wizard-map.composable';
import { useApi } from '../../../services';
import { computed, onMounted, ref, type Ref } from 'vue';
import type { Extent } from 'ol/extent';
import type {
  HeightmapPeriod,
  HeightmapWizardData,
  HeightmapWizardParcelData,
  HeightmapWizardParcelItem,
  ParcelHeightmapItem
} from '../models';
import { HEIGHT_MAP_WIZARD_FORM_CONFIG } from '../config/heightmap-wizard-form.config';
import { useInfoModal } from '../../../composables';
import { useIsLoading } from '@core';
import type { HeightmapSaveWarningModalConfig } from '../config';
import { HeightmapSaveWarningModal } from '../components';
import { createHeightmapLayerConfig, getCoverageStateByValue } from '../utils';
import { useColorMapStore } from '../../colormap';
import { storeToRefs } from 'pinia';
import { HeightmapCoverageState } from '../enums';
import { useRouter } from 'vue-router';
import { Permission, useAuthStore, type LayerGroup, type LayerGroupLayer } from '@shared';
import HeightmapEmptyCoverageModal from '../components/HeightmapEmptyCoverageModal.vue';
import type { DialogModalConfig } from '@common';
import HeightmapApiErrorModal from '../components/HeightmapApiErrorModal.vue';
import { getDemoOffer } from '../../parcel/utils/offer.utils.js';

const MODULE_PERMISSION = Permission.MODULE_MAP;

export const useHeightmapWizard = () => {
  const modals = useModals();
  const notifications = useNotifications();
  const { map, zoomToExtent, addLayer, removeLayer } = useHeightmapWizardMap();
  const { openInfoModal } = useInfoModal('maximap.heightmap.info', 'heightmap.label.infoTitle');
  const router = useRouter();

  const parcelApi = useApi().parcel;
  const heightmapApi = useApi().heightmap;

  const authStore = useAuthStore();
  const { isDemoOfferVisible } = storeToRefs(authStore);

  const colorMapStore = useColorMapStore();
  const { heightmapColorMap } = storeToRefs(colorMapStore);

  const wizardForm = createForm(HEIGHT_MAP_WIZARD_FORM_CONFIG);
  const { valid: wizardFormValid, validate: validateWizardForm, getChild } = useForm(wizardForm.element);

  const { isLoading: isQueryLoading, handlePromiseLoading } = useIsLoading();
  const isParcelsLoading = ref(false);

  const heightmapWizardData: Ref<HeightmapWizardData> = ref({
    parcels: []
  });
  const parcelHeightmaps: Ref<ParcelHeightmapItem[]> = ref([]);
  const queriedPeriod: Ref<HeightmapPeriod | undefined> = ref();
  const hasParcelsWithoutQueryData = ref(false);

  const wizardFormDataChanged = computed(() => {
    if (!queriedPeriod.value) {
      return true;
    }
    const dateFromField = getChild('dateFrom') as FormElement;
    const dateToField = getChild('dateTo') as FormElement;

    return queriedPeriod.value.dateFrom !== dateFromField.value || queriedPeriod.value.dateTo !== dateToField.value;
  });

  const queryHeightmapDataEnabled = computed(() => {
    if (heightmapWizardData.value.parcels.length > 0) {
      validateWizardForm();
    }
    return (
      heightmapWizardData.value.parcels.length > 0 &&
      wizardFormValid.value &&
      (hasParcelsWithoutQueryData.value || wizardFormDataChanged.value)
    );
  });

  const canSaveHeightmaps = computed(() => {
    return heightmapWizardData.value.parcels.some(
      (p) =>
        p.coverageState === HeightmapCoverageState.COVERED ||
        p.coverageState === HeightmapCoverageState.PARTIALLY_COVERED
    );
  });

  const loadHeightmapCandidate = async (heightmapId: string) => {
    const candidateData = await heightmapApi.getHeightmapCandidate(heightmapId);
    return candidateData.data;
  };

  const loadParcelsOutline = async (parcelIds: string[]) => {
    const response = await parcelApi.getParcelsOutline(parcelIds);
    return response.data;
  };

  const checkSomeParcelHasCoverage = (parcelHeightMaps: ParcelHeightmapItem[]) => {
    return parcelHeightMaps.some((p) => p.coverage > 0);
  };

  const addHeightmapVectorLayers = async () => {
    parcelHeightmaps.value.forEach(async (parcelHeightmap) => {
      if (parcelHeightmap.heightmapLayerGroups && parcelHeightmap.heightmapLayerGroups.length) {
        return;
      }

      await buildHeightmapVectorLayers(parcelHeightmap);

      if (parcelHeightmap.heightmapLayerGroups) {
        parcelHeightmap.heightmapLayerGroups.forEach((layerGroup) => {
          addLayer(layerGroup);
        });
      }
    });
  };

  const loadOffers = async () => {
    const demoOffer = isDemoOfferVisible.value ? await getDemoOffer() : null;
    const offers = demoOffer ? [demoOffer] : [];

    const response = await parcelApi.getOffers(MODULE_PERMISSION);
    offers.push(...response.data);
    return offers;
  };

  const loadParcelGroups = async () => {
    const response = await parcelApi.getParcelGroups(MODULE_PERMISSION);
    return response.data;
  };

  const openParcelSelectorModal = async () => {
    try {
      isParcelsLoading.value = true;

      const offers = await loadOffers();

      const parcelGroups = await loadParcelGroups();

      modals.open<ParcelGroupModalConfig>(ParcelGroupModal, {
        title: 'statistics.modal.openParcelsTitle',
        confirmBtnLabel: 'button.open',
        text: '',
        width: '1000px',
        contentHeight: '500px',
        persistent: true,
        scrollable: true,
        translate: true,
        hideGroupForm: true,
        offers: offers,
        selectedParcelIds: heightmapWizardData.value.parcels.map((p) => p.id),
        parcelGroups: parcelGroups,
        maxSelectableParcels: 20,
        showHeightmapCoverage: true,
        inclineLayersBlocked: false,
        onConfirm: async (parcels) => {
          await addSelectedParcels(parcels);
          addParcelsOutlineToMap();
        },
        onCancel: () => {
          isParcelsLoading.value = false;
        }
      });
    } catch {
      notifications.showError('statistics.wizard.messages.loadOffersError');
      isParcelsLoading.value = false;
    }
  };

  const addParcelsOutlineToMap = async () => {
    const parcelIds = heightmapWizardData.value.parcels.filter((p) => !p.outlineLoaded).map((p) => p.id);
    const outline = await loadParcelsOutline(parcelIds);

    outline.features.forEach((feature) => {
      const parcel = heightmapWizardData.value.parcels.find((p) => p.id === feature.id);
      if (parcel) {
        parcel.outlineLoaded = true;

        const mapLayerGroup = createVectorMapLayerGroup({
          id: parcel.id,
          name: parcel.name,
          mapLayerConfigs: [
            {
              id: parcel.id as string,
              classification: '',
              type: feature.type,
              name: '',
              featureProjection: MAP_PROJECTION_3857,
              dataProjection: MAP_PROJECTION_4326,
              rawData: feature as any,
              zoneStyleConfig: {
                defaultStyle: {
                  color: 'transparent',
                  strokeWidth: 3
                }
              }
            }
          ]
        });

        addLayer(mapLayerGroup);
      }
    });
  };

  const addSelectedParcels = async (parcels: HeightmapWizardParcelItem[]) => {
    try {
      deleteUnselectedParcels(parcels);
      for (const parcel of parcels) {
        await setupSelectedParcel(parcel);
      }
      fitParcelsExtent();
      isParcelsLoading.value = false;
    } catch (e) {
      notifications.showError('statistics.wizard.messages.openParcelsError');
      isParcelsLoading.value = false;
    }
  };

  const setupSelectedParcel = async (parcel: HeightmapWizardParcelItem) => {
    const alreadyAdded = !!heightmapWizardData.value.parcels.find((p) => p.id === parcel.id);
    if (alreadyAdded) return;

    const heightmapWizardParcel: HeightmapWizardParcelData = {
      id: parcel.id,
      name: parcel.name,
      area: parcel.area || 0,
      svg: parcel.svg,
      mapData: null,
      rasterLayerGroups: [],
      vectorLayerGroups: [],
      blocked: parcel.blocked,
      bbox: transformExtent(parcel.bbox ?? [], MAP_PROJECTION_4326, MAP_PROJECTION_3857) as [
        number,
        number,
        number,
        number
      ],
      assignedHeightmapCoverage: parcel.heightmapCoverage,
      outlineLoaded: false
    };

    hasParcelsWithoutQueryData.value = true;
    heightmapWizardData.value.parcels.push(heightmapWizardParcel);
  };

  const fitParcelsExtent = () => {
    const extent = heightmapWizardData.value.parcels.reduce((prev: Extent, cur) => mergeExtents(prev, cur.bbox), []);
    zoomToExtent(extent);
  };

  const saveHeightmaps = () => {
    modals.open<HeightmapSaveWarningModalConfig>(HeightmapSaveWarningModal, {
      title: 'heightmap.label.finalizeAssignments',
      width: '600px',
      parcels: heightmapWizardData.value.parcels,
      onConfirm: async (parcelIds) => {
        await saveConfirmedHeightmaps(parcelIds);
      },
      onCancel: () => {
        isParcelsLoading.value = false;
      }
    });
  };

  const removeOldLayersIfHasNewLayers = (parcelHeigthmapResponse: ParcelHeightmapItem[]) => {
    parcelHeightmaps.value.forEach((parcelHeightmap) => {
      const found = parcelHeigthmapResponse.find((p) => p.id === parcelHeightmap.id);
      if (found) {
        parcelHeightmap.heightmapLayerGroups?.forEach((layerGroup) => {
          removeLayer(layerGroup.id);
        });
      }
    });
  };

  const saveConfirmedHeightmaps = async (parcelIds: string[]) => {
    try {
      const params = {
        ...wizardForm.value.value,
        parcels: parcelIds
      };

      await heightmapApi.saveHeightmap(params);
      notifications.showSuccess('heightmap.messages.saveHeightmapsSuccess');
      await router.push({ name: 'heightmaps' });
    } catch (e) {
      notifications.showError('heightmap.messages.saveHeightmapsError');
    }
  };

  const buildHeightmapVectorLayers = async (parcelHeightmap: ParcelHeightmapItem) => {
    const group = parcelHeightmap.heightmap as LayerGroup;
    const subLayersData = group.layers ? [...group.layers] : [];
    if (!subLayersData.length) return;

    parcelHeightmap.heightmapLayerGroups = [];

    for (const subLayerMasterData of subLayersData) {
      const mapLayerConfigs: VectorMapLayerConfig[] = [await createLayerConfig(subLayerMasterData, '', true)];

      const layerGroup = createVectorMapLayerGroup({
        id: subLayerMasterData.id,
        name: subLayerMasterData.name,
        mapLayerConfigs: mapLayerConfigs,
        customData: {
          parcelName: parcelHeightmap.name,
          year: subLayerMasterData.year,
          uploadDate: group.createdAt,
          canDelete: group.canDelete
        }
      });

      parcelHeightmap.heightmapLayerGroups.push(layerGroup);
    }
  };

  const createLayerConfig = async (
    subLayerMasterData: LayerGroupLayer,
    classification: any,
    enabled = true
  ): Promise<VectorMapLayerConfig> => {
    const subLayerData = await loadHeightmapCandidate(subLayerMasterData.id);
    const subLayerColorMap = colorMapStore.findColorMap(subLayerMasterData.colorMap || '');

    return createHeightmapLayerConfig(
      subLayerMasterData,
      classification,
      true,
      enabled,
      subLayerData,
      subLayerColorMap
    );
  };

  const deleteUnselectedParcels = (selectedParcels: HeightmapWizardParcelItem[]) => {
    const selectedParcelIds = selectedParcels.map((p) => p.id);

    const parcelsToRemove = heightmapWizardData.value.parcels.filter((p) => !selectedParcelIds.includes(p.id));
    parcelsToRemove.forEach((parcel) => {
      removeParcel(parcel.id);
    });
  };

  const setNewHeightmapDataToParcels = (parcelHeightmapItems: ParcelHeightmapItem[]) => {
    heightmapWizardData.value.parcels.forEach((parcel) => {
      const parcelHeightmap = parcelHeightmapItems.find((p) => p.id === parcel.id);
      if (parcelHeightmap) {
        parcel.newHeightmapCoverage = parcelHeightmap.coverage;
        parcel.coverageState = getCoverageStateByValue(parcelHeightmap.coverage, heightmapColorMap.value.breaks);
        parcel.modem = parcelHeightmap.modem;
      }
    });
  };

  const removeParcel = (parcelId: string) => {
    removeParcelFromParcels(parcelId);
    removeParcelHeightmapData(parcelId);
    fitParcelsExtent();
  };

  const removeParcelFromParcels = (parcelId: string) => {
    const index = heightmapWizardData.value.parcels.findIndex((p) => p.id === parcelId);
    if (index === -1) {
      return;
    }
    heightmapWizardData.value.parcels.splice(index, 1);
  };

  const removeParcelHeightmapData = (parcelId: string) => {
    const parcelHeightMap = parcelHeightmaps.value.find((p) => p.id === parcelId);
    if (parcelHeightMap) {
      parcelHeightMap.heightmapLayerGroups?.forEach((layerGroup) => {
        removeLayer(layerGroup.id);
      });
    }
    removeLayer(parcelId);
    parcelHeightmaps.value = parcelHeightmaps.value.filter((p) => p.id !== parcelId);
  };

  const queryHeightmapData = async () => {
    let parcelIds = heightmapWizardData.value.parcels.map((p) => p.id);

    if (!wizardFormDataChanged.value) {
      parcelIds = parcelIds.filter((id) => !parcelHeightmaps.value.find((p) => p.id === id));
    }

    const params = {
      ...wizardForm.value.value,
      parcels: parcelIds
    };

    try {
      const response = await notifications.promise(handlePromiseLoading(heightmapApi.queryHeightmapData(params)));

      if (response.data) {
        removeOldLayersIfHasNewLayers(response.data);

        if (wizardFormDataChanged.value) {
          parcelHeightmaps.value = response.data;
        } else {
          parcelHeightmaps.value = parcelHeightmaps.value.concat(response.data);
        }
        hasParcelsWithoutQueryData.value = false;

        queriedPeriod.value = {
          ...wizardForm.value.value
        };

        setNewHeightmapDataToParcels(response.data);
        addHeightmapVectorLayers();
        if (!checkSomeParcelHasCoverage(response.data)) {
          modals.open<DialogModalConfig>(HeightmapEmptyCoverageModal, {
            title: 'heightmap.modal.emptyCoverageModal.title',
            width: '800px'
          });
        }
      }
    } catch (e) {
      modals.open<DialogModalConfig>(HeightmapApiErrorModal, {
        title: 'heightmap.modal.apiErrorModal.title',
        width: '800px'
      });
    }
  };

  onMounted(() => {
    openInfoModal();
  });

  return {
    wizardForm,
    heightmapWizardData,
    map,
    isParcelsLoading,
    queryHeightmapDataEnabled,
    isQueryLoading,
    canSaveHeightmaps,
    zoomToExtent,
    removeParcel,
    saveHeightmaps,
    openParcelSelectorModal,
    fitParcelsExtent,
    openInfoModal,
    queryHeightmapData
  };
};
